import type {ChangeSpec} from '@codemirror/state'
import type {Command} from '@codemirror/view'
import type {List, Root} from 'mdast'

import {
  buildFirstMarkerSyntax,
  buildItemContinuationSyntax,
  buildNextMarkerSyntax,
} from '../../../language-tools/markdown/list-utils'
import {MarkdownLine} from '../../../language-tools/markdown/markdown-line'
import {
  getChildrenOverlappingRange,
  getNodesContainingRange,
} from '../../../language-tools/syntax-tree/node-range-utils'
import {isPositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getMarkdownTree} from '../../extensions/markdown-parser'
import {createText} from '../../extensions/single-line-editor'
import {removeList} from './remove-list'
import {positionedItems} from './utils'

/**
 * Finds the position of the end of the blockquote prefix at the given `depth`, where `depth` is the number of
 * blockquote ancestors. For example, if the line is `> > > > hello` and the depth is `2`, will return `3` (the index
 * after the location of the second '>' prefix).
 */
function findBlockquotePrefixEnd(line: MarkdownLine, depth: number) {
  if (depth === 0) return line.start
  for (const prefix of line.prefixes)
    if (prefix.tokenType === 'blockQuotePrefix') {
      depth--
      if (depth === 0) return prefix.position?.end.offset ?? line.start
    }
  return line.start
}

/** Generate changes to convert an entire list to a different type. Does not actually execute the transaction. */
function buildListConversion(tree: Root, doc: string, list: List, toOrdered: boolean): ChangeSpec[] {
  const changes: ChangeSpec[] = []

  let lastMarker: string | null = null

  for (const item of positionedItems(list.children)) {
    const lines = MarkdownLine.linesAtRange(tree, doc, item.position.start.offset, item.position.end.offset - 1)
    for (const [i, line] of lines.entries()) {
      if (i === 0) {
        const prefix = line?.prefixes.find(p => p.tokenType === 'listItemPrefix')
        if (!prefix || !isPositionedNode(prefix)) continue
        const marker: string = lastMarker ? buildNextMarkerSyntax(lastMarker) : buildFirstMarkerSyntax(toOrdered)
        changes.push({
          from: prefix.position.start.offset,
          to: prefix.position.end.offset,
          insert: createText(marker),
        })
        lastMarker = marker
      } else {
        // If the length of the marker has changed, we need to also change the amount of trailing indent that all
        // the remaining lines have
        const indentPrefix = line?.prefixes.find(p => p.tokenType === 'listItemIndent')
        if (
          !indentPrefix ||
          !lastMarker ||
          !isPositionedNode(indentPrefix) ||
          indentPrefix.raw.length === lastMarker.length
        )
          continue
        changes.push({
          from: indentPrefix.position.start.offset,
          to: indentPrefix.position.end.offset,
          insert: createText(buildItemContinuationSyntax(lastMarker)),
        })
      }
    }
  }
  return changes
}

/**
 * Either create, replace, or remove lists depending on the selection and contents.
 */
const formatList =
  (ordered: boolean): Command =>
  view => {
    const tree = getMarkdownTree(view.state)
    const doc = view.state.doc.toString()
    const selection = view.state.selection.main

    // If the selection is fully contained inside of a matching list, this is a remove or replace operation:
    const deepestListContainingSelection = Array.from(
      getNodesContainingRange(tree, selection.from, selection.to, true),
    ).findLast(n => n.type === 'list')
    if (deepestListContainingSelection) {
      if ((deepestListContainingSelection.ordered ?? false) !== ordered) {
        // Non-matching list: replace it with the new type
        view.dispatch({
          changes: buildListConversion(tree, doc, deepestListContainingSelection, ordered),
          scrollIntoView: true,
          userEvent: 'input',
        })
        return true
      } else {
        return removeList(view)
      }
    }

    // Otherwise, create lists around all the selected content while converting any lists inside the selection:

    /** Array of top-level affected nodes to operate on. */
    let selectedNodes = getChildrenOverlappingRange(tree, selection.from, selection.to)
    /** Track the number of wrapping blockquote elements, so we can determine where on a line to insert the list marker. */
    let blockquoteDepth = 0

    // If selection is fully inside a blockquote, dig into it rather than making the whole quote one list item.
    // Blockquotes are the only case we handle here for now. There's an argument to make for taking a similar approach
    // for HTML (particularly details/summary), but that gets more complicated since the <details> tag could be on the
    // same line as the content, and then where do you put the marker? I'd rather hold off on that until asked for it.
    while (selectedNodes.length === 1 && selectedNodes[0]!.type === 'blockquote') {
      selectedNodes = getChildrenOverlappingRange(selectedNodes[0], selection.from, selection.to)
      blockquoteDepth++
    }

    let lastMarker: string | null = null
    const changes: ChangeSpec[] = []

    for (const [nodeIndex, node] of selectedNodes.entries()) {
      // If the node is a list, convert that entire list (even items outside the selection) to the new type
      if (node.type === 'list') {
        changes.push(...buildListConversion(tree, doc, node, ordered))
        continue
      }

      const nodeLines = isPositionedNode(node)
        ? MarkdownLine.linesAtRange(tree, doc, node.position.start.offset, node.position.end.offset - 1)
        : []

      for (const [lineIndex, line] of nodeLines.entries()) {
        // Blank lines don't get a marker or indentation
        if (line.isBlank) continue

        // Normally the insertion point would be the beginning of the line, but inside a blockquote it will go after the
        // blockquote prefix(es)
        const from = findBlockquotePrefixEnd(line, blockquoteDepth)

        // In paragraphs, ignore lines that don't overlap the selection
        if (node.type === 'paragraph' && !line.overlaps(selection.from, selection.to)) continue

        // For paragraphs, every line becomes a new list item (allowing easily creating a list from several adjacent
        // lines). For all other node types (blocks), we only create a single item by applying the prefix to first
        // line and adding hanging indent (continuation syntax) to the remaining lines
        if (lineIndex === 0 || node.type === 'paragraph') {
          const prevLine = line.previous()
          const prevLineIsBlank = prevLine && prevLine.isBlank
          // Before the start of a list, ensure the previous line is blank
          if (lastMarker === null && prevLine && !prevLine.isBlank) {
            changes.push({
              from: line.start - 1,
              insert: createText('\n'),
            })
          }

          // If the previous line is blank, restart numbering
          const marker: string =
            lastMarker === null || prevLineIsBlank ? buildFirstMarkerSyntax(ordered) : buildNextMarkerSyntax(lastMarker)

          changes.push({
            from,
            insert: createText(marker),
          })
          lastMarker = marker
        } else if (lastMarker) {
          changes.push({
            from,
            insert: createText(buildItemContinuationSyntax(lastMarker)),
          })
        }

        // On the last line of the last node, insert a blank line if necessary
        if (nodeIndex === selectedNodes.length - 1 && lineIndex === nodeLines.length - 1) {
          const nextLine = line.next()
          if (nextLine && !nextLine.isBlank) {
            changes.push({
              from: line.end,
              insert: createText('\n'),
            })
          }
        }
      }
    }

    view.dispatch({changes, scrollIntoView: true, userEvent: 'input'})
    return true
  }

export const formatUnorderedList: Command = formatList(false)

export const formatOrderedList: Command = formatList(true)
